import { useState, useRef, useEffect, useCallback } from 'react'
import { message } from 'antd'
import _ from 'lodash'
import {
  generateVideoDeepAI,
  generateLumaVideo,
  getTaskStatus,
  assembleScenesWithAudio
} from '../../../../../services/api/aiVideoGeneration'
import { FRAME_ROLES, getShotFrameUrl } from '../../ShotFrames/frameUtils'

const getFormattedPrompt = (prompt, formData = {}) => {
  const selectedCharacters = formData?.selectedCharacter || []
  const formattedPrompt = selectedCharacters.reduce((acc, char, index) => {
    const placeholder = `__character${index + 1}__`
    return acc.replace(
      new RegExp(placeholder, 'g'),
      char.current.replace(/__/g, '')
    )
  }, prompt)
  return formattedPrompt
}

const getLumaPayload = (videoData, negative_prompt = '', formData, shot) => {
  return {
    prompt: getFormattedPrompt(videoData.prompt, formData),
    img_url: videoData.image_url,
    ...(videoData.last_frame_url && {
      last_frame_url: videoData.last_frame_url
    }),
    duration: videoData.duration,
    video_id: videoData.video_id,
    shot: shot || null,
    character_map: formData?.characterMap || null,
    outfit_map: formData?.outfitMap || null,
    wardrobe: formData?.wardrobe || null,
    outfits_reference: formData?.outfits_reference || null,
    character_voices: formData?.characterVoices || null,
    video_mode: formData?.audioMode || 'narration'
  }
}

const extractTaskId = response => {
  const data = response?.data || response || {}

  return (
    data?.task_id ||
    data?.taskId ||
    data?.['task-id'] ||
    data?.output?.task_id ||
    data?.output?.taskId ||
    data?.output?.['task-id'] ||
    null
  )
}

const VIDEO_TASK_PENDING_STATUSES = ['pending', 'processing']
const VIDEO_TASK_SUCCESS_STATUSES = ['success', 'completed', 'done']
const VIDEO_TASK_ERROR_STATUSES = ['failed', 'error']
const STOPPED_VIDEO_TASK = '__STOPPED_VIDEO_TASK__'

const getShotVideoGenerationStatus = shot => {
  if (
    shot?.videoGenerationStatus &&
    typeof shot.videoGenerationStatus === 'object'
  ) {
    return {
      ...shot.videoGenerationStatus,
      status: shot.videoGenerationStatus.status
        ? shot.videoGenerationStatus.status.toLowerCase()
        : null
    }
  }

  if (shot?.statusId) {
    return {
      id: shot.statusId,
      status: 'pending'
    }
  }

  return null
}

const isVideoTaskPending = status =>
  VIDEO_TASK_PENDING_STATUSES.includes(status?.toLowerCase?.())

export const useVideoGeneration = ({
  scenes,
  setScenes,
  formData,
  videoProvider,
  videoId
}) => {
  const [videoGenerating, setVideoGenerating] = useState([])
  const [
    generatingVideoForAllScenes,
    setGeneratingVideoForAllScenes
  ] = useState(false)
  const activeTaskMapRef = useRef({})
  const completedTaskStatusRef = useRef({})
  const stoppedTaskIdsRef = useRef({})
  const stoppedShotKeysRef = useRef({})
  const pollingDelayCancelRef = useRef({})
  const startingVideoGenerationRef = useRef({})
  const generationRequestTokenRef = useRef({})

  const updateShotDataRef = useRef(
    _.debounce((sceneId, shotId, updatesOrKey, value) => {
      setScenes(prevScenes =>
        prevScenes.map(scene => {
          if (scene.id === sceneId) {
            return {
              ...scene,
              shots: scene.shots.map(shot => {
                if (shot.id === shotId) {
                  if (
                    typeof updatesOrKey === 'object' &&
                    updatesOrKey !== null
                  ) {
                    return { ...shot, ...updatesOrKey }
                  }
                  return { ...shot, [updatesOrKey]: value }
                }
                return shot
              })
            }
          }
          return scene
        })
      )
    }, 500)
  )

  const markShotAsGenerating = useCallback(
    (sceneId, shotId, statusId = null) => {
      setScenes(prevScenes => {
        let hasChanges = false

        const nextScenes = prevScenes.map(scene => {
          if (scene.id !== sceneId) return scene

          const nextShots = scene.shots.map(shot => {
            if (shot.id !== shotId) return shot

            const currentTaskStatus = getShotVideoGenerationStatus(shot)
            const currentStatusIsPending = isVideoTaskPending(
              currentTaskStatus?.status
            )
            const nextVideoGenerationStatus = {
              id:
                statusId ||
                (currentStatusIsPending ? currentTaskStatus?.id : null) ||
                null,
              status: 'pending'
            }
            const shouldUpdate =
              shot.progress !== 0 ||
              shot.errorGeneratingVideo ||
              shot.statusId != null ||
              JSON.stringify(currentTaskStatus) !==
                JSON.stringify(nextVideoGenerationStatus)

            if (!shouldUpdate) {
              return shot
            }

            hasChanges = true
            return {
              ...shot,
              progress: 0,
              statusId: null,
              videoGenerationStatus: nextVideoGenerationStatus,
              errorGeneratingVideo: false
            }
          })

          return hasChanges ? { ...scene, shots: nextShots } : scene
        })

        return hasChanges ? nextScenes : prevScenes
      })
    },
    [setScenes]
  )

  const finalizeVideoGeneration = useCallback(
    (
      sceneId,
      shotId,
      videoLink,
      effectiveDuration,
      taskId,
      statusOverride = null
    ) => {
      setScenes(prevScenes =>
        prevScenes.map(scene => {
          if (scene.id !== sceneId) return scene

          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId
                ? (() => {
                    const currentTaskStatus = getShotVideoGenerationStatus(shot)
                    if (
                      taskId &&
                      isVideoTaskPending(currentTaskStatus?.status) &&
                      !currentTaskStatus?.id
                    ) {
                      return shot
                    }
                    if (
                      taskId &&
                      currentTaskStatus?.id &&
                      currentTaskStatus.id !== taskId
                    ) {
                      return shot
                    }

                    const nextStatus =
                      statusOverride || (videoLink ? 'success' : 'error')
                    const currentHistory = shot.videos_history || []
                    const sourceFrames = `${shot.imageSrc ||
                      ''}::${getShotFrameUrl(shot, FRAME_ROLES.end) || ''}`
                    const nextHistory =
                      videoLink &&
                      !currentHistory.some(entry => entry?.url === videoLink)
                        ? [
                            ...currentHistory,
                            {
                              url: videoLink,
                              timestamp: Date.now(),
                              duration_at_generation: effectiveDuration,
                              source_image: shot.imageSrc,
                              source_frames: sourceFrames
                            }
                          ]
                        : currentHistory

                    return {
                      ...shot,
                      progress: videoLink
                        ? 100
                        : nextStatus === 'stopped'
                        ? 0
                        : shot.progress,
                      statusId: null,
                      videoGenerationStatus: {
                        id:
                          taskId ||
                          getShotVideoGenerationStatus(shot)?.id ||
                          null,
                        status: nextStatus
                      },
                      ...(videoLink
                        ? {
                            vidSrc: videoLink,
                            video_source_image: shot.imageSrc,
                            video_source_frames: sourceFrames,
                            errorGeneratingVideo: false,
                            video_duration: effectiveDuration,
                            calculated_duration: null,
                            videos_history: nextHistory
                          }
                        : {
                            errorGeneratingVideo: nextStatus !== 'stopped'
                          })
                    }
                  })()
                : shot
            )
          }
        })
      )
    },
    [setScenes]
  )

  const removeGeneratingMarker = useCallback(
    (sceneId, shotId, taskId = null) => {
      setVideoGenerating(prevStatus =>
        (prevStatus || []).filter(status => {
          const isTargetShot =
            status.sceneId === sceneId && status.shotId === shotId
          if (!isTargetShot) return true
          if (!taskId) return false
          return !status.taskId || status.taskId !== taskId
        })
      )
    },
    []
  )

  const addGeneratingMarker = useCallback((sceneId, shotId, taskId = null) => {
    setVideoGenerating(prevStatus => {
      const existingIndex = (prevStatus || []).findIndex(
        status => status.sceneId === sceneId && status.shotId === shotId
      )
      if (existingIndex === -1) {
        return [...(prevStatus || []), { sceneId, shotId, taskId }]
      }

      const existing = prevStatus[existingIndex]
      if (!taskId || existing.taskId === taskId) {
        return prevStatus
      }

      return prevStatus.map((status, index) =>
        index === existingIndex ? { ...status, taskId } : status
      )
    })
  }, [])

  const waitForVideoTaskSuccess = useCallback(async (taskId, onProgress) => {
    const waitForNextPoll = timeoutMs =>
      new Promise(resolve => {
        if (stoppedTaskIdsRef.current[taskId]) {
          resolve()
          return
        }

        const timer = setTimeout(resolve, timeoutMs)
        pollingDelayCancelRef.current[taskId] = () => {
          clearTimeout(timer)
          resolve()
        }
      }).finally(() => {
        delete pollingDelayCancelRef.current[taskId]
      })

    try {
      if (!taskId) return null
      const delayMs = 10000

      while (true) {
        try {
          if (stoppedTaskIdsRef.current[taskId]) {
            return STOPPED_VIDEO_TASK
          }

          const statusResponse = await getTaskStatus(taskId)
          const data = statusResponse?.data || null
          const status = data?.status ? data.status.toLowerCase() : null

          if (stoppedTaskIdsRef.current[taskId]) {
            return STOPPED_VIDEO_TASK
          }

          if (data?.progress && onProgress) {
            onProgress(data.progress)
          }

          if (VIDEO_TASK_SUCCESS_STATUSES.includes(status)) {
            return data?.output || ''
          }
          if (VIDEO_TASK_ERROR_STATUSES.includes(status)) {
            return null
          }
          if (
            status === 'pending' &&
            (data?.progress === '100%' || data?.progress === 100)
          ) {
            await waitForNextPoll(delayMs)
            continue
          }
        } catch (err) {
          console.error('Error fetching video task status, retrying:', err)
          return null
        }
        await waitForNextPoll(delayMs)
      }
    } catch (err) {
      return null
    }
  }, [])

  const trackVideoTask = useCallback(
    async (sceneId, shotId, taskId, effectiveDuration) => {
      if (!taskId) return null

      const taskKey = `${sceneId}::${shotId}::${taskId}`
      const shotKey = `${sceneId}::${shotId}`
      const isStopped = () =>
        stoppedShotKeysRef.current[shotKey] || stoppedTaskIdsRef.current[taskId]

      if (activeTaskMapRef.current[taskKey]) {
        addGeneratingMarker(sceneId, shotId, taskId)
        return activeTaskMapRef.current[taskKey]
      }

      if (completedTaskStatusRef.current[taskId]) {
        return completedTaskStatusRef.current[taskId].videoLink || null
      }

      addGeneratingMarker(sceneId, shotId, taskId)

      const taskPromise = Promise.resolve()
        .then(() =>
          waitForVideoTaskSuccess(taskId, progress => {
            if (isStopped()) return

            const progressVal = parseInt(progress) || 0
            updateShotDataRef.current(sceneId, shotId, 'progress', progressVal)
          })
        )
        .then(videoLink => {
          if (videoLink === STOPPED_VIDEO_TASK || isStopped()) {
            completedTaskStatusRef.current[taskId] = {
              status: 'stopped',
              videoLink: null
            }
            finalizeVideoGeneration(
              sceneId,
              shotId,
              null,
              effectiveDuration,
              taskId,
              'stopped'
            )
            return null
          }

          completedTaskStatusRef.current[taskId] = {
            status: videoLink ? 'success' : 'error',
            videoLink: videoLink || null
          }
          finalizeVideoGeneration(
            sceneId,
            shotId,
            videoLink,
            effectiveDuration,
            taskId
          )
          return videoLink
        })
        .finally(() => {
          delete activeTaskMapRef.current[taskKey]
          removeGeneratingMarker(sceneId, shotId, taskId)
        })

      activeTaskMapRef.current[taskKey] = taskPromise
      return taskPromise
    },
    [
      addGeneratingMarker,
      finalizeVideoGeneration,
      removeGeneratingMarker,
      waitForVideoTaskSuccess
    ]
  )

  const handleStopVideoGeneration = useCallback(
    (sceneId, shotId) => {
      let taskId = null
      const shotKey = `${sceneId}::${shotId}`

      const scene = (scenes || []).find(s => s.id === sceneId)
      const shot = scene?.shots?.find(sh => sh.id === shotId)
      const videoGenerationStatus = getShotVideoGenerationStatus(shot)
      taskId = videoGenerationStatus?.id || null
      const activeTaskIds = Object.keys(activeTaskMapRef.current)
        .filter(activeTaskKey => activeTaskKey.startsWith(`${shotKey}::`))
        .map(activeTaskKey => activeTaskKey.split('::')[2])
        .filter(Boolean)
      const taskIdsToStop = Array.from(
        new Set([taskId, ...activeTaskIds].filter(Boolean))
      )

      stoppedShotKeysRef.current[shotKey] = true
      generationRequestTokenRef.current[shotKey] =
        (generationRequestTokenRef.current[shotKey] || 0) + 1
      delete startingVideoGenerationRef.current[shotKey]

      taskIdsToStop.forEach(id => {
        stoppedTaskIdsRef.current[id] = true
        completedTaskStatusRef.current[id] = {
          status: 'stopped',
          videoLink: null
        }

        if (pollingDelayCancelRef.current[id]) {
          pollingDelayCancelRef.current[id]()
        }
      })

      removeGeneratingMarker(sceneId, shotId)

      setScenes(prevScenes =>
        prevScenes.map(sceneItem => {
          if (sceneItem.id !== sceneId) return sceneItem

          return {
            ...sceneItem,
            shots: sceneItem.shots.map(shotItem =>
              shotItem.id === shotId
                ? {
                    ...shotItem,
                    progress: 0,
                    statusId: null,
                    errorGeneratingVideo: false,
                    videoGenerationStatus: {
                      id:
                        taskId ||
                        taskIdsToStop[0] ||
                        getShotVideoGenerationStatus(shotItem)?.id ||
                        null,
                      status: 'stopped'
                    }
                  }
                : shotItem
            )
          }
        })
      )

      message.info('Video generation stopped for this shot.')
    },
    [removeGeneratingMarker, scenes, setScenes]
  )

  useEffect(() => {
    ;(scenes || []).forEach(scene => {
      ;(scene.shots || []).forEach(shot => {
        const videoGenerationStatus = getShotVideoGenerationStatus(shot)
        const localCompletedTask =
          completedTaskStatusRef.current[videoGenerationStatus?.id]
        const effectiveDuration =
          shot.calculated_duration || shot.video_duration

        if (
          localCompletedTask &&
          isVideoTaskPending(videoGenerationStatus?.status)
        ) {
          finalizeVideoGeneration(
            scene.id,
            shot.id,
            localCompletedTask.videoLink,
            effectiveDuration,
            videoGenerationStatus.id,
            localCompletedTask.status
          )
          return
        }

        const isPendingTask =
          videoGenerationStatus?.id &&
          isVideoTaskPending(videoGenerationStatus?.status) &&
          !localCompletedTask

        // Resume pending tasks even if an older vidSrc exists from a prior generation.
        if (!isPendingTask || shot?.is_static) {
          return
        }

        addGeneratingMarker(scene.id, shot.id, videoGenerationStatus.id)

        if (shot.statusId) {
          markShotAsGenerating(scene.id, shot.id, videoGenerationStatus.id)
        }

        trackVideoTask(
          scene.id,
          shot.id,
          videoGenerationStatus.id,
          effectiveDuration
        )
      })
    })
  }, [
    scenes,
    addGeneratingMarker,
    finalizeVideoGeneration,
    markShotAsGenerating,
    trackVideoTask
  ])

  const handleRegenerateVideo = async (sceneId, shotId) => {
    const shotKey = `${sceneId}::${shotId}`
    let requestToken = null

    try {
      const scene = scenes.find(s => s.id === sceneId)
      const shot = scene.shots?.find(sh => sh.id === shotId)
      delete stoppedShotKeysRef.current[shotKey]

      if (shot?.is_static) {
        message.info(
          'Static shots use the image directly, so no video is generated.'
        )
        return
      }

      const effectiveDuration = shot.calculated_duration || shot.video_duration
      const currentTaskStatus = getShotVideoGenerationStatus(shot)

      if (isVideoTaskPending(currentTaskStatus?.status)) {
        addGeneratingMarker(sceneId, shotId, currentTaskStatus.id || null)
        if (currentTaskStatus?.id) {
          trackVideoTask(
            sceneId,
            shotId,
            currentTaskStatus.id,
            effectiveDuration
          )
        }
        return
      }

      if (startingVideoGenerationRef.current[shotKey]) {
        return
      }

      const lastFrameUrl = getShotFrameUrl(shot, FRAME_ROLES.end)

      requestToken = (generationRequestTokenRef.current[shotKey] || 0) + 1
      generationRequestTokenRef.current[shotKey] = requestToken
      startingVideoGenerationRef.current[shotKey] = true

      addGeneratingMarker(sceneId, shotId)
      markShotAsGenerating(sceneId, shotId)

      const videoData = {
        prompt: shot.video_prompt,
        image_url: shot.imageSrc,
        ...(lastFrameUrl && { last_frame_url: lastFrameUrl }),
        ...(videoProvider !== 'DeepAI' && { duration: effectiveDuration }),
        video_id: videoId,
        shot: shot || null,
        character_map: formData?.characterMap || null,
        outfit_map: formData?.outfitMap || null,
        wardrobe: formData?.wardrobe || null,
        outfits_reference: formData?.outfits_reference || null
      }

      const request =
        videoProvider === 'DeepAI' ? generateVideoDeepAI : generateLumaVideo
      const payload =
        videoProvider === 'DeepAI'
          ? videoData
          : getLumaPayload(videoData, shot.negative_prompt, formData, shot)

      const response = await request(payload)
      const taskId = extractTaskId(response)

      if (!taskId) {
        throw new Error('Video generation did not return a status ID')
      }

      const isStaleRequest =
        generationRequestTokenRef.current[shotKey] !== requestToken

      if (isStaleRequest || stoppedShotKeysRef.current[shotKey]) {
        stoppedTaskIdsRef.current[taskId] = true
        completedTaskStatusRef.current[taskId] = {
          status: 'stopped',
          videoLink: null
        }

        if (isStaleRequest && !stoppedShotKeysRef.current[shotKey]) {
          return
        }

        finalizeVideoGeneration(
          sceneId,
          shotId,
          null,
          effectiveDuration,
          taskId,
          'stopped'
        )
        return
      }

      delete stoppedTaskIdsRef.current[taskId]
      markShotAsGenerating(sceneId, shotId, taskId)
      trackVideoTask(sceneId, shotId, taskId, effectiveDuration)
        .then(videoLink => {
          if (stoppedTaskIdsRef.current[taskId]) return

          if (videoLink) {
            message.success(`Video generated successfully!`)
          } else {
            message.error('Video generation failed')
          }
        })
        .catch(error => {
          console.error('Error polling regenerated video:', error)
          message.error('Error regenerating video: ' + error.message)
        })
    } catch (error) {
      removeGeneratingMarker(sceneId, shotId)
      setScenes(prevScenes =>
        prevScenes.map(scene => {
          if (scene.id !== sceneId) return scene

          return {
            ...scene,
            shots: scene.shots.map(shot =>
              shot.id === shotId
                ? {
                    ...shot,
                    errorGeneratingVideo: true,
                    statusId: null,
                    videoGenerationStatus: {
                      id: getShotVideoGenerationStatus(shot)?.id || null,
                      status: 'error'
                    }
                  }
                : shot
            )
          }
        })
      )
      message.error('Error regenerating video: ' + error.message)
      console.error('Error regenerating video:', error)
    } finally {
      if (
        requestToken &&
        generationRequestTokenRef.current[shotKey] === requestToken
      ) {
        delete startingVideoGenerationRef.current[shotKey]
      }
    }
  }

  const handleGenerateFullVideo = async (targetScenes = null) => {
    try {
      if (!targetScenes) {
        setGeneratingVideoForAllScenes(true)
      }

      const scenesToProcess = targetScenes || scenes
      const affectedSceneIds = new Set()

      const videoQueue = scenesToProcess.flatMap(scene =>
        scene.shots
          .filter(shot => !shot.is_static)
          .map(shot => {
            affectedSceneIds.add(scene.id)
            return { scene, shot }
          })
      )

      if (videoQueue.length === 0) {
        message.info('No video clips need generation.')
        return
      }

      const promises = videoQueue.map(async ({ scene, shot }) => {
        try {
          const sceneId = scene.id
          const shotId = shot.id
          const shotKey = `${sceneId}::${shotId}`
          delete stoppedShotKeysRef.current[shotKey]

          addGeneratingMarker(sceneId, shotId)
          markShotAsGenerating(sceneId, shotId)

          setScenes(prevScenes =>
            prevScenes.map(s => {
              if (s.id === sceneId) {
                return {
                  ...s,
                  shots: s.shots.map(sh =>
                    sh.id === shotId ? { ...sh, progress: 0 } : sh
                  )
                }
              }
              return s
            })
          )

          const effectiveDuration =
            shot.calculated_duration || shot.video_duration
          const lastFrameUrl = getShotFrameUrl(shot, FRAME_ROLES.end)

          const videoData = {
            prompt: getFormattedPrompt(shot.video_prompt, formData),
            image_url: shot.imageSrc,
            ...(lastFrameUrl && { last_frame_url: lastFrameUrl }),
            ...(videoProvider !== 'DeepAI' && {
              duration: effectiveDuration
            }),
            video_id: videoId,
            shot: shot || null,
            character_map: formData?.characterMap || null,
            outfit_map: formData?.outfitMap || null,
            wardrobe: formData?.wardrobe || null,
            outfits_reference: formData?.outfits_reference || null
          }

          const request =
            videoProvider === 'DeepAI' ? generateVideoDeepAI : generateLumaVideo
          const payload =
            videoProvider === 'DeepAI'
              ? videoData
              : getLumaPayload(videoData, shot.negative_prompt, formData, shot)

          const response = await request(payload)
          const taskId = extractTaskId(response)

          if (!taskId) {
            throw new Error('Video generation did not return a status ID')
          }

          if (stoppedShotKeysRef.current[shotKey]) {
            stoppedTaskIdsRef.current[taskId] = true
            completedTaskStatusRef.current[taskId] = {
              status: 'stopped',
              videoLink: null
            }
            finalizeVideoGeneration(
              sceneId,
              shotId,
              null,
              effectiveDuration,
              taskId,
              'stopped'
            )
            return
          }

          delete stoppedTaskIdsRef.current[taskId]
          markShotAsGenerating(sceneId, shotId, taskId)
          await trackVideoTask(sceneId, shotId, taskId, effectiveDuration)
        } catch (error) {
          console.error(
            `Error generating video for scene ${scene.id}, shot ${shot.id}:`,
            error
          )
          removeGeneratingMarker(scene.id, shot.id)
          setScenes(prevScenes =>
            prevScenes.map(s => {
              if (s.id === scene.id) {
                return {
                  ...s,
                  shots: s.shots.map(sh =>
                    sh.id === shot.id
                      ? {
                          ...sh,
                          errorGeneratingVideo: true,
                          statusId: null,
                          videoGenerationStatus: {
                            id: getShotVideoGenerationStatus(sh)?.id || null,
                            status: 'error'
                          }
                        }
                      : sh
                  )
                }
              }
              return s
            })
          )
        }
      })

      await Promise.all(promises)
      message.success('Video generation process completed.')
    } catch (error) {
      console.error('Error starting video generation:', error)
      message.error('Error starting video generation: ' + error.message)
    } finally {
      setGeneratingVideoForAllScenes(false)
    }
  }

  const handleRetryFailedVideos = () => {
    const scenesWithFailures = scenes
      .map(scene => {
        const failedShots = scene.shots.filter(shot => {
          const isInQueue = videoGenerating.some(
            v => v.sceneId === scene.id && v.shotId === shot.id
          )
          if (isInQueue) return false
          if (shot.is_static) return false
          return (
            shot.errorGeneratingVideo || (!shot.vidSrc && !shot.isGenerating)
          )
        })
        if (failedShots.length > 0) {
          return {
            ...scene,
            shots: failedShots
          }
        }
        return null
      })
      .filter(s => s !== null)

    if (scenesWithFailures.length === 0) {
      message.info('No failed videos to retry.')
      return
    }

    handleGenerateFullVideo(scenesWithFailures)
  }

  return {
    videoGenerating,
    generatingVideoForAllScenes,
    handleGenerateFullVideo,
    handleRegenerateVideo,
    handleStopVideoGeneration,
    handleRetryFailedVideos,
    updateShotDataRef
  }
}
